import {
    ApiBaseService,
    type EnhancedFetch,
    type EnhancedFetchOptions,
    type PaginatedFetch,
    type PaginatedFetchOptions,
} from "./ApiBaseService";
import type { Connection } from "~/models/Connection";
import type { ApiResponse } from "~/types/ApiResponse";
import type { FetchOptions } from "ofetch";
import type { ExternalObject } from "~/models/ExternalObject";
import {
    faArrowsSpin,
    faClockRotateLeft,
    faEdit,
    faSync,
    faTrash,
} from "@fortawesome/pro-regular-svg-icons";
import { faBolt } from "@fortawesome/pro-solid-svg-icons";
import type { AutomationObject } from "~/models/Automation";
import type { FilterGroup } from "~/components/automation/step/FilterDisplay.vue";

export default class ConnectionService extends ApiBaseService {
    public list(
        options?: Partial<PaginatedFetchOptions>,
    ): PaginatedFetch<Connection[]> {
        return this.paginate<Connection[]>(
            this.route("api.connections.index", "/connections"),
            options as PaginatedFetchOptions,
        );
    }

    public find(
        uuid: string,
        options?: EnhancedFetchOptions,
    ): EnhancedFetch<Connection> {
        return this.enhancedFetch<Connection>(
            this.route("api.connections.show", "/connections/{connection}", {
                connection: uuid,
            }),
            options,
        );
    }

    public findRoute(uuid: string) {
        return this.route("api.connections.show", "/connections/{connection}", {
            connection: uuid,
        });
    }

    public findRequest(
        uuid: string,
        options?: FetchOptions,
    ): Promise<ApiResponse<Connection>> {
        return this.request(this.findRoute(uuid), options);
    }

    public create(
        data?: Partial<Connection>,
    ): Promise<ApiResponse<Connection>> {
        return this.request(
            this.route("api.connections.store", "/connections"),
            {
                method: "POST",
                body: data,
            },
        );
    }

    public objects(uuid: string): EnhancedFetch<unknown> {
        return this.enhancedFetch(
            this.route(
                "api.connections.objects",
                "/connections/{uuid}/objects",
                { uuid },
            ),
            {
                method: "GET",
            },
        );
    }

    public objectsRoute(uuid: string) {
        return this.route(
            "api.connections.objects",
            "/connections/{uuid}/objects",
            { uuid },
        );
    }

    public objectsRequest(
        uuid: string,
        options?: FetchOptions,
    ): Promise<ApiResponse<unknown>> {
        return this.request(this.objectsRoute(uuid), options);
    }

    public getObjects(
        uuid: string,
        options?: FetchOptions,
    ): Promise<ApiResponse<AutomationObject[]>> {
        return this.request(this.objectsRoute(uuid), options);
    }

    public getObject(
        uuid: string,
        name: string,
        options?: FetchOptions,
    ): Promise<ApiResponse<AutomationObject>> {
        return this.request(
            this.route(
                "api.connections.objects.show",
                "/connections/{uuid}/objects/{name}",
                { uuid, name },
            ),
            options,
        );
    }

    public getExternalObjects(
        uuid: string,
    ): Promise<ApiResponse<ExternalObject[]>> {
        return this.request(
            this.route("api.external_objects.index", "/external_objects"),
            {
                query: {
                    pagination: "none",
                    connection_id: uuid,
                    sorts: "name",
                },
            },
        );
    }

    public update(
        uuid: string,
        data: Partial<Connection>,
    ): Promise<ApiResponse<Connection>> {
        return this.request(
            this.route("api.connections.update", "/connections/{connection}", {
                connection: uuid,
            }),
            {
                method: "PATCH",
                body: data,
            },
        );
    }

    public reconnect(uuid: string): Promise<ApiResponse<Connection>> {
        return this.request(
            this.route(
                "api.connections.reconnect",
                "/connections/{connection}/reconnect",
                {
                    connection: uuid,
                },
            ),
            {
                method: "POST",
            },
        );
    }

    public updateObject(
        connection_id: string,
        object_id: number,
        data: Partial<ExternalObject>,
    ): Promise<ApiResponse<ExternalObject>> {
        return this.request(
            this.route(
                "api.connections.objects.update",
                "/connections/{connection}/objects/{object}",
                { connection: connection_id, object: object_id },
            ),
            {
                method: "PATCH",
                body: data,
            },
        );
    }

    public restoreDefaultObject(
        connection_id: string,
        object_id?: number,
    ): Promise<ApiResponse<ExternalObject[]>> {
        return this.request(
            this.route(
                "api.connections.objects.restore",
                "/connections/{connection}/objects/restore",
                { connection: connection_id },
            ),
            {
                method: "PATCH",
                body: {
                    object_id,
                },
            },
        );
    }

    public async provision(
        uuid: string,
    ): Promise<ApiResponse<Connection> | undefined> {
        try {
            const response = await this.request<ApiResponse<Connection>>(
                this.route(
                    "api.connections.provision",
                    "/connections/{connection}/provision",
                    {
                        connection: uuid,
                    },
                ),
                {
                    method: "POST",
                },
            );

            useToast().add({
                icon: "i-heroicons-check",
                title: response.message,
            });

            return response;
        } catch {
            return undefined;
        }
    }

    public delete(uuid: string): Promise<null> {
        return this.request(
            this.route("api.connections.destroy", "/connections/{connection}", {
                connection: uuid,
            }),
            {
                method: "DELETE",
            },
        );
    }

    public searchField(
        uuid: string,
        object: string,
        field: string,
        config: Partial<{
            pagination: "none";
            search: string;
            operator: string;
            label_key: string;
            value_key: string;
            filters: FilterGroup;
            page: number;
            limit: number;
        }> = {},
    ): Promise<ApiResponse<Record<string, any>>> {
        return this.request<ApiResponse<Record<string, any>>>(
            this.route(
                "api.field_maps.search",
                "connections/{connection}/objects/{object}/fields/{field}/search",
                {
                    connection: uuid,
                    object,
                    field,
                },
            ),
            {
                method: "POST",
                body: config,
            },
        );
    }

    public dropdown(
        connection: Connection,
        options: {
            deletable?: ref<Connection | undefined>;
        } = {},
    ) {
        const items = [];
        const related = [];
        const api = useApi();
        const route = useRoute();

        if (route.name !== "apps-id-connectionId") {
            items.push({
                label: "Edit",
                icon: faEdit,
                to: {
                    name: "apps-id-connectionId",
                    params: {
                        id: connection.name,
                        connectionId: connection.uuid,
                    },
                },
            });
        }

        if (connection.can_be_provisioned) {
            items.push({
                label: "Sync",
                icon: faSync,
                click: () =>
                    api.connections.provision(connection.uuid as string),
            });
        }

        if (connection.can_restore_default_objects && !connection.is_preset) {
            items.push({
                label: "Restore to defaults",
                icon: faClockRotateLeft,
                click: () => {
                    useToast().add({
                        title: "Restore to defaults?",
                        description:
                            "Are you sure you want to restore the connection objects to the default configuration?",
                        icon: "i-heroicons-exclamation-circle",
                        color: "primary",
                        actions: [
                            {
                                label: "Restore",
                                color: "primary",
                                click: async () => {
                                    try {
                                        useEventBus(
                                            "connection-objects-restoring",
                                        ).emit(connection.uuid as string);
                                        await api.connections.restoreDefaultObject(
                                            connection.uuid as string,
                                        );
                                        useToast().add({
                                            title: "Connection objects restored",
                                            description:
                                                "The connection objects have been restored to the default configuration",
                                            color: "green",
                                        });

                                        useEventBus(
                                            "connection-objects-updated",
                                        ).emit();
                                    } catch (error) {
                                        console.error(error);
                                    }
                                },
                            },
                            {
                                label: "Cancel",
                            },
                        ],
                    });
                },
            });
        }

        if (options.deletable && !connection.is_preset) {
            items.push({
                label: "Delete",
                icon: faTrash,
                click: () =>
                    options.deletable && (options.deletable.value = connection),
            });
        }

        related.push({
            label: "Related",
            disabled: true,
        });

        related.push({
            label: "Events",
            icon: faArrowsSpin,
            to: {
                name: "apps-id-connectionId-events",
                params: {
                    id: connection.name,
                    connectionId: connection.uuid,
                },
            },
        });

        related.push({
            label: "Runs",
            icon: faArrowsSpin,
            to: {
                name: "apps-id-connectionId-runs",
                params: {
                    id: connection.name,
                    connectionId: connection.uuid,
                },
            },
        });

        related.push({
            label: "Tasks",
            icon: faBolt,
            to: {
                name: "apps-id-connectionId-tasks",
                params: {
                    id: connection.name,
                    connectionId: connection.uuid,
                },
            },
        });

        return [items, related];
    }
}
